import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { RightBridgeApiService } from '../services/right-bridge-api.service';
import { EnvironmentService } from '../services/environment.service';
import { SessionStorageService } from '../services/session-storage.service';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ApiResponse, GridResults } from '../models/general-models';

@Injectable({ providedIn: 'root' })
export class ReportingService {
  private http = inject(HttpClient);
  private rbs = inject(RightBridgeApiService);
  private envSvc = inject(EnvironmentService);
  private ss = inject(SessionStorageService);

  private environment;
  private baseUrl: string;
  private org: string;

  constructor() {
    this.environment = this.envSvc.get();
    this.baseUrl = this.environment.apiBase;
    this.org = this.environment.org;
  }

  getLoadHistory(): Observable<ApiResponse<GridResults>> {
    const url = `${this.baseUrl}/Reports/LoadHistory`;
    return this.rbs.postRequest<ApiResponse<GridResults>>(url);
  }

  getUnitProfileReport(change): Observable<Blob | null> {
    const id = this.ss.get('session');
    const url = `${this.baseUrl}/UnitProfile/HistoryProfile/${change}`;
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', 'pp')
      .set('X-RB-O', this.org)
      .set('accept', 'application/json');

    return this.http
      .get(url, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError(error => {
          return throwError(() => error.error || 'Server error');
        })
      );
  }

  getUnitProfileChanges(
    app,
    unit,
    startDate,
    endDate
  ): Observable<Blob | null> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', app)
      .set('X-RB-O', this.org)
      .set('Accept', 'application/pdf');
    const url = `${this.rbs.baseUrl}/Reports/UnitProfileChanges/${unit}?startDate=${startDate}&endDate=${endDate}`;

    return this.http
      .post(url, null, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError(error => {
          return throwError(() => error.error || 'Server error');
        })
      );
  }

  getBridgeDensity(unit, score): Observable<ApiResponse<GridResults>> {
    const url = `${this.baseUrl}/Reports/BridgeDensity/${unit}/${score}`;
    return this.rbs.postRequest<ApiResponse<GridResults>>(url);
  }

  getBridgeAction(
    unit,
    startDate?,
    endDate?
  ): Observable<ApiResponse<GridResults>> {
    let url = `${this.baseUrl}/Reports/BridgeAction?unitID=${unit}&level=UNIT_CHILDREN&detailLevel=COMBINED`;
    if (startDate && endDate) {
      url += `&startDate=${startDate}&endDate=${endDate}`;
    }
    return this.rbs.postRequest<ApiResponse<GridResults>>(url);
  }

  getBridgeActionDetail(
    unit,
    startDate,
    endDate
  ): Observable<ApiResponse<GridResults>> {
    let url = `${this.baseUrl}/Reports/BridgeActionDetail?unitID=${unit}`;
    if (startDate && endDate) {
      url += `&startDate=${startDate}&endDate=${endDate}`;
    }
    return this.rbs.postRequest<ApiResponse<GridResults>>(url);
  }

  getSalesTracking(salesReqData): Observable<ApiResponse<GridResults>> {
    const url = `${this.baseUrl}/Reports/SalesTracking?unitID=${salesReqData.unit}&userID=${salesReqData.user}&activityType=${salesReqData.at}&saleType=${salesReqData.st}&repIntersect=${salesReqData.ri}&daysGap=${salesReqData.gap}&startDate=${salesReqData.startDate}&endDate=${salesReqData.endDate}`;
    return this.rbs.postRequest<ApiResponse<GridResults>>(url);
  }

  getReasonText(unit, app): Observable<Blob | null> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', app)
      .set('X-RB-O', this.org)
      .set('accept', 'application/xslx');
    const url = `${this.baseUrl}/Reports/ReasonText?unitID=${unit}`;

    return this.http
      .post(url, null, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError(error => {
          return throwError(() => error.error || 'Server error');
        })
      );
  }

  getMutualFund(unit, start, end): Observable<Blob | null> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', 'pp')
      .set('X-RB-O', this.org);
    const url = `${this.baseUrl}/Reports/MFUsage?unitID=${unit}&sd=${start}&ed=${end}`;

    return this.http
      .get(url, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError(error => {
          return throwError(() => error.error || 'Server error');
        })
      );
  }

  getRepExceptions(
    start,
    end,
    id,
    app,
    unit
  ): Observable<ApiResponse<GridResults>> {
    const sess = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', app)
      .set('X-RB-O', this.org);
    const url = `${this.baseUrl}/Reports/RepExceptions?startDate=${start}&endDate=${end}&alertID=${id}&unitID=${unit}`;
    return this.rbs.postRequest<ApiResponse<GridResults>>(
      url,
      null,
      reqHeaders
    );
  }

  getRoleRightHistoryReport(startDate, endDate): Observable<Blob | null> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org)
      .set('Accept', 'application/pdf');
    const url = `${this.rbs.baseUrl}/Reports/RoleRightChanges?startDate=${startDate}&endDate=${endDate}`;

    return this.http
      .post(url, null, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError(error => {
          return throwError(() => error.error || 'Server error');
        })
      );
  }

  getUnitManagementChanges(unit, startDate, endDate): Observable<Blob | null> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org)
      .set('Accept', 'application/pdf');
    const url = `${this.rbs.baseUrl}/Reports/UnitManagementChanges/${unit}?startDate=${startDate}&endDate=${endDate}`;

    return this.http
      .post(url, null, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError(error => {
          return throwError(() => error.error || 'Server error');
        })
      );
  }

  getComparisonReport(reportData, solution): Observable<Blob | null> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org)
      .set('Accept', 'application/pdf');

    const url = `${this.rbs.baseUrl}/Reports/Comparison`;

    return this.http
      .post(url, reportData, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError(error => {
          return throwError(() => error.error || 'Server error');
        })
      );
  }

  submitReportRequest(reportData): Observable<Blob | null> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org)
      .set('Accept', 'application/pdf');

    const url = `${this.rbs.baseUrl}/Reports/RequestReport`;

    return this.http
      .post(url, reportData, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError(error => {
          return throwError(() => error.error || 'Server error');
        })
      );
  }

  getUnitSessionActivity(
    start,
    end,
    activityCode,
    selectedUnit
  ): Observable<Blob | null> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', 'pp')
      .set('X-RB-O', this.org)
      .set('Accept', 'application/csv');

    const url = `${this.baseUrl}/Reports/UnitSessionActivity?startDate=${start}&endDate=${end}&activityCodes=${activityCode}&unitID=${selectedUnit}`;
    return this.http
      .post(url, null, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return throwError(() => error.error || 'Server error');
        })
      );
  }
}
